import React, { useState, useEffect } from 'react'
import { useStyles } from './FlyOutOnboardingProductDetails.styles'
import { FlyOutProductDetailsForm } from '../../../AddEditProductFormComponents/FlyOutProductDetailsForm/FlyOutProductDetailsForm'
import { defaultNow } from '../../../../common/utils/dateTimeNow'
import { awsRequirementsLink } from '../../../../common/utils/constants'
import { FlyOutProductDetailSupportInformation } from '../../../AddEditProductFormComponents/FlyOutProductDetailSupportInformation/FlyOutProductDetailSupportInformation'
import { FormikProps, useFormikContext } from 'formik'
import {
  Carousel,
  LabraToolTipV2,
  ModalConfirmV2,
  ModalSizeVariants,
  PlanType,
} from '@labrav/react-components'
import {
  FlyoutProductSections,
  FlyoutProductAddEditSectionName,
  FlyoutListingProductType,
} from '../../../../types/addEditProduct'
import { FlyOutProductDiscoverability } from '../../../AddEditProductFormComponents/FlyOutProductDiscoverability/FlyOutProductDiscoverability'
import { FlyOutProductEula } from '../../../AddEditProductFormComponents/FlyOutProductEula/FlyOutProductEula'
import { FlyOutProductRefundPolicy } from '../../../AddEditProductFormComponents/FlyOutProductRefundPolicy/FlyOutProductRefundPolicy'
import { FlyOutProductAdhere } from '../../../AddEditProductFormComponents/FlyOutProductAdhere/FlyOutProductAdhere'
import { FlyOutProductAllowlistedAWSAccounts } from '../../../AddEditProductFormComponents/FlyOutProductAllowlistedAWSAccounts/FlyOutProductAllowlistedAWSAccounts'
import { productDetailsImages } from '../../../../common/utils/productCreationUtil'
import { FormLockedType } from '../../../../types/FormLockedType'
import { FlyOutMigrationProductsList } from '../../../AddEditProductFormComponents/FlyOutMigrationProductsList/FlyOutMigrationProductsList'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAws } from '@fortawesome/free-brands-svg-icons'
import { FlyOutProductAiTools } from '../../../AddEditProductFormComponents/FlyOutProductAgenticAi/FlyOutProductAiTools'
import { useLocation } from 'react-router-dom'
import { FlyOutAWSProductListingType } from '../../../../modules/productsListing/types'

export interface FlyOutProductDetailsProps {
  detailView?: boolean
  detailViewTooltip?: string
  productView?: boolean
  isImpartner?: boolean
}

export const FlyOutProductDetails: React.FC<
  FormLockedType & FlyOutProductDetailsProps
> = ({
  isFormLocked,
  detailView,
  detailViewTooltip,
  productView,
  isImpartner,
}) => {
  const { classes } = useStyles()
  const [indexModal, setIndexModal] = useState(0)
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const awsProductType = params.get('awsProductType')
  const handleRefLinkClick = (index: number) => {
    setIndexModal(index)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }
  const noOps = () => void 0

  const { values, setFieldValue } =
    useFormikContext() as FormikProps<FlyoutProductSections>
  const currentIsAgentic = values.generalInformation?.isAgenticProduct === true
  const derivedIsAgentic =
    awsProductType === FlyOutAWSProductListingType.AGENTIC_AI
  useEffect(() => {
    if (currentIsAgentic !== derivedIsAgentic) {
      setFieldValue('generalInformation.isAgenticProduct', derivedIsAgentic)
    }
  }, [awsProductType, derivedIsAgentic, currentIsAgentic, setFieldValue])
  const productType = values.productDetails.productType
  const isProfessionalService = productType === PlanType.PROFESSIONAL_SERVICES
  const requirementsLink = isProfessionalService ? '' : awsRequirementsLink
  const isMigrated =
    values?.[FlyoutProductAddEditSectionName.CLOUD_PRODUCT_DETAILS]
      .listingType === FlyoutListingProductType.MIGRATED
  return (
    <div className={classes.container} data-testid={'product-detail-page'}>
      {isMigrated ? (
        <FlyOutMigrationProductsList isImpartner={isImpartner} />
      ) : (
        <>
          <FlyOutProductDetailsForm
            isFormLocked={isFormLocked}
            now={defaultNow}
            requirementsLink={requirementsLink}
            handleRefLinkClick={handleRefLinkClick}
            detailView={detailView}
            detailViewTooltip={detailViewTooltip}
          />
          <FlyOutProductDetailSupportInformation
            requirementsLink={requirementsLink}
            isFormLocked={isFormLocked}
            handleRefLinkClick={handleRefLinkClick}
            detailView={detailView}
            detailViewTooltip={detailViewTooltip}
          />
          <FlyOutProductDiscoverability
            requirementsLink={requirementsLink}
            isFormLocked={isFormLocked}
            detailView={detailView}
            detailViewTooltip={detailViewTooltip}
          />
          {!isProfessionalService ? (
            <>
              <FlyOutProductEula
                requirementsLink={requirementsLink}
                now={defaultNow}
                isFormLocked={isFormLocked}
                handleRefLinkClick={handleRefLinkClick}
                detailView={detailView}
                detailViewTooltip={detailViewTooltip}
              />
              <FlyOutProductRefundPolicy
                requirementsLink={requirementsLink}
                isFormLocked={isFormLocked}
                handleRefLinkClick={handleRefLinkClick}
                detailView={detailView}
                detailViewTooltip={detailViewTooltip}
              />
              <LabraToolTipV2
                tooltipPlacement="bottom-start"
                description={detailViewTooltip}
                followCursor={detailView}
              >
                <FlyOutProductAllowlistedAWSAccounts
                  requirementsLink={requirementsLink}
                  isFormLocked={isFormLocked}
                  detailView={detailView}
                />
              </LabraToolTipV2>
              {awsProductType === FlyOutAWSProductListingType.AGENTIC_AI && (
                <FlyOutProductAiTools
                  isFormLocked={isFormLocked}
                  now={defaultNow}
                  requirementsLink={requirementsLink}
                  handleRefLinkClick={handleRefLinkClick}
                  detailView={detailView}
                  detailViewTooltip={detailViewTooltip}
                />
              )}
            </>
          ) : (
            <></>
          )}
          {!productView && (
            <FlyOutProductAdhere
              requirementsLink={requirementsLink}
              sectionName={FlyoutProductAddEditSectionName.GENERAL_INFORMATION}
              isFormLocked={isFormLocked}
            />
          )}

          <ModalConfirmV2
            title={`${productDetailsImages[indexModal].heading}`}
            description={''}
            icon={
              <FontAwesomeIcon
                icon={faAws}
                style={{ height: '16px', width: '16px' }}
              />
            }
            content={
              <Carousel
                showHeading={false}
                carouselData={productDetailsImages}
                handleImageClick={noOps}
                setCurrentIndex={setIndexModal}
                currentIndex={indexModal}
              />
            }
            open={open}
            titleAcceptButton={''}
            titleCancelButton={''}
            onDecline={handleClose}
            variant={ModalSizeVariants.large}
            hideAcceptButton={true}
            hideCancelButton={true}
            onAccept={noOps}
          />
        </>
      )}
    </div>
  )
}
